import { useState, useEffect } from "react";
import { TextBox, type TextBoxChangeEvent } from "@progress/kendo-react-inputs";
import { ICON_BASE_PATH } from "../../../../utils/config";
import { t } from "i18next";

interface FirstNameProps {
  label?: string;
  value: string;
  onChange: (val: string) => void;
  validationMessage?: string;
  showStarLabel?: boolean;
  max?: number;
  disable?: boolean;
  triggerValidation?: boolean;
  fieldName?: string;
  onValidate?: (fieldName: string, isValid: boolean) => void;
  valid?: boolean;
}

const FirstName: React.FC<FirstNameProps> = ({
  label = "First Name",
  value,
  onChange,
  validationMessage,
  showStarLabel = true,
  max,
  disable = false,
  triggerValidation = false,
  fieldName = "firstName",
  onValidate,
}) => {
  const [error, setError] = useState<string>("");
  const [touched, setTouched] = useState(false);

  const sanitize = (s: string) => s.replace(/[^a-zA-Z]/g, "");

  const validate = (val: string): string => {
    if (!val) return `${t("please_enter_first_name")}.`;
    if (val.length < 3)
      return `${label} ${t("must_be_at_least")} 3 ${t("characters")}.`;
    if (val.length > 30)
      return `${label} ${t("cannot_exceed")} 30 ${t("characters")}.`;
    return "";
  };

  const updateValidation = (val: string) => {
    const newError = validate(val);
    setError(newError);
    onValidate?.(fieldName, !newError);
    return newError;
  };

  const handleChange = (e: TextBoxChangeEvent) => {
    const raw = String(e.value ?? "");
    let filtered = sanitize(raw);
    if (max && filtered.length > max) filtered = filtered.slice(0, max);

    onChange(filtered);

    if (touched && error) {
      const newError = validate(filtered);
      if (!newError) setError("");
    }
  };

  const handleBlur = () => {
    setTouched(true);
    updateValidation(value);
  };

  const handleFocus = () => {
    // Keep existing error visible but don’t revalidate
  };

  const isInvalid = !!error && touched;

  useEffect(() => {
    if (triggerValidation) {
      setTouched(true);
      updateValidation(value);
    }
  }, [triggerValidation]);

  useEffect(() => {
    if (validationMessage) {
      setError(validationMessage);
      setTouched(true);
      onValidate?.(fieldName, false);
    } else if (touched) {
      updateValidation(value);
    }
  }, [validationMessage]);

  return (
    <div className="gx_input_container">
      <label
        className={`input-label gx-font-semibold ${value ? "floating" : ""}`}
      >
        {t(label)}
        {showStarLabel && (
          <span
            className={`gx_input_star ${
              value ? "gx_input_star_float" : "gx_input_star_normal"
            }`}
          >
            *
          </span>
        )}
      </label>

      <div className={`gx_input_cont ${isInvalid ? "invalid" : "valid"}`}>
        <img src={`${ICON_BASE_PATH}name-input.svg`} alt="Name" />
        <TextBox
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          type="text"
          autoComplete="off"
          max={max}
          disabled={disable}
          className="gx-textbox-css"
        />
      </div>

      {isInvalid && (
        <div className="gx_input_validationmessage gx-font-regular">
          {error}
        </div>
      )}
    </div>
  );
};

export default FirstName;
